/**
 * Item 配置块 JSON Schema 定义
 */
export const itemSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  $id: 'https://o2world.ai/schemas/config-types/item.json',
  title: 'Item 配置',
  description: 'Item（物品）配置数据结构',
  type: 'object',
  required: ['item_id', 'name', 'item_type'],
  properties: {
    item_id: {
      type: 'string',
      title: 'Item ID',
      description: 'Item 唯一标识符',
      pattern: '^item_[a-zA-Z0-9_]+$',
      minLength: 6,
      maxLength: 50,
      examples: ['item_evidence_01', 'item_health_potion', 'item_key_master'],
    },
    name: {
      type: 'string',
      title: '物品名称',
      description: '物品在游戏中显示的名称',
      minLength: 1,
      maxLength: 100,
      examples: ['证据碎片', '生命药水', '万能钥匙'],
    },
    description: {
      type: 'string',
      title: '物品描述',
      description: '物品的详细描述',
      minLength: 0,
      maxLength: 500,
    },
    item_type: {
      type: 'string',
      title: '物品类型',
      description: '物品的分类',
      enum: ['证据', '消耗品', '装备', '任务物品', '钥匙', '材料', '其他'],
    },
    rarity: {
      type: 'string',
      title: '稀有度',
      description: '物品的稀有度等级',
      enum: ['普通', '罕见', '稀有', '史诗', '传说'],
      default: '普通',
    },
    icon: {
      type: 'string',
      title: '图标',
      description: '物品图标的资源路径或 URL',
      maxLength: 200,
    },
    stackable: {
      type: 'boolean',
      title: '可堆叠',
      description: '是否可以堆叠',
      default: true,
    },
    max_stack: {
      type: 'integer',
      title: '最大堆叠数量',
      description: '单格最多可堆叠的数量',
      minimum: 1,
      maximum: 9999,
      default: 99,
    },
    weight: {
      type: 'number',
      title: '重量',
      description: '物品重量（影响负重）',
      minimum: 0,
      default: 0,
    },
    value: {
      type: 'integer',
      title: '价值',
      description: '物品的基础价值（金币）',
      minimum: 0,
      default: 0,
    },
    effects: {
      type: 'array',
      title: '效果',
      description: '使用物品时触发的效果',
      items: {
        type: 'object',
        required: ['effect_type'],
        properties: {
          effect_type: {
            type: 'string',
            title: '效果类型',
            enum: ['恢复生命值', '恢复士气', '增益', '减益', '触发事件', '其他'],
          },
          value: {
            type: 'number',
            title: '效果数值',
            description: '效果的数值大小',
          },
          duration: {
            type: 'integer',
            title: '持续时间',
            description: '效果持续时间（秒）',
            minimum: 0,
          },
          description: {
            type: 'string',
            title: '效果描述',
            maxLength: 200,
          },
        },
      },
    },
    requirements: {
      type: 'object',
      title: '使用要求',
      description: '使用物品的前置条件',
      properties: {
        level: {
          type: 'integer',
          title: '最低等级',
          minimum: 1,
        },
        attributes: {
          type: 'object',
          title: '属性要求',
          description: '需要的最低属性值',
          additionalProperties: {
            type: 'integer',
            minimum: 0,
          },
        },
      },
    },
    acquisition: {
      type: 'object',
      title: '获取方式',
      description: '物品的获取途径',
      properties: {
        purchasable: {
          type: 'boolean',
          title: '可购买',
          default: false,
        },
        droppable: {
          type: 'boolean',
          title: '可掉落',
          default: false,
        },
        tradable: {
          type: 'boolean',
          title: '可交易',
          default: true,
        },
        quest_reward: {
          type: 'boolean',
          title: '任务奖励',
          default: false,
        },
      },
    },
    consumable: {
      type: 'boolean',
      title: '可消耗',
      description: '使用后是否消耗',
      default: false,
    },
    metadata: {
      type: 'object',
      title: '元数据',
      description: '扩展元数据',
      additionalProperties: true,
    },
  },
  additionalProperties: false,
} as const

export type ItemConfig = {
  item_id: string
  name: string
  description?: string
  item_type: string
  rarity?: string
  icon?: string
  stackable?: boolean
  max_stack?: number
  weight?: number
  value?: number
  effects?: Array<{
    effect_type: string
    value?: number
    duration?: number
    description?: string
  }>
  requirements?: {
    level?: number
    attributes?: Record<string, number>
  }
  acquisition?: {
    purchasable?: boolean
    droppable?: boolean
    tradable?: boolean
    quest_reward?: boolean
  }
  consumable?: boolean
  metadata?: Record<string, any>
}
